/**
 * Routes.
 *
 * Every route that touches data is wrapped in RequirePermission. That is not
 * the control — the server is — but it means a user who follows a stale link
 * gets a clear explanation rather than a screen full of failed requests.
 */

import type { ReactElement } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSession } from './lib/session';
import { useTenant } from './lib/tenant';
import { Shell, RequirePermission } from './components/Shell';
import { ErrorBoundary, Loading } from './components/ui';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { ApprovalsPage } from './pages/Approvals';
import { ConsolidationPage } from './pages/Consolidation';
import { RequisitionsPage, RequisitionNewPage, RequisitionDetailPage } from './pages/Requisitions';
import { OrdersPage, OrderDetailPage } from './pages/Orders';
import { ReceiptsPage } from './pages/Receipts';
import { SuppliersPage, SupplierDetailPage } from './pages/Suppliers';
import { InvoicesPage, InvoiceDetailPage } from './pages/Invoices';
import { PaymentsPage, PaymentRunDetailPage } from './pages/Payments';
import { SalesPage } from './pages/Sales';
import { DebtorsPage } from './pages/Debtors';
import { LedgerPage, JournalDetailPage } from './pages/Ledger';
import { BudgetsPage } from './pages/Budgets';
import { CashPage } from './pages/Cash';
import { AssetsPage } from './pages/Assets';
import { ProjectsPage } from './pages/Projects';
import { ReportsPage } from './pages/Reports';
import { ContractsPage, ContractDetailPage } from './pages/Contracts';
import { PlanningPage } from './pages/Planning';
import { AssistantPage } from './pages/Assistant';
import { ClosePage } from './pages/Close';
import { IntegrationsPage } from './pages/Integrations';
import { MigrationPage } from './pages/Migration';
import { AuditPage } from './pages/Audit';
import { SelfServicePage } from './pages/SelfService';
import { SupportPage } from './pages/Support';
import { AdminPage } from './pages/Admin';

export function App() {
  const { user, loading } = useSession();
  const { ready } = useTenant();
  const location = useLocation();

  if (loading || !ready) {
    return (
      <div className="login-page" style={{ placeItems: 'center' }}>
        <Loading label="Starting" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const guard = (anyOf: string[], element: ReactElement) => (
    <RequirePermission anyOf={anyOf}>{element}</RequirePermission>
  );

  return (
    <Shell>
      {/*
        Keyed on the path so that walking away from a broken screen clears the
        error: the boundary remounts on navigation rather than trapping the user
        on the failure until they reload the browser.
      */}
      <ErrorBoundary key={location.pathname}>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/approvals" element={guard(['workflow.view'], <ApprovalsPage />)} />
        <Route path="/consolidation" element={guard(['report.run.all'], <ConsolidationPage />)} />

        <Route path="/requisitions" element={guard(['requisition.create', 'requisition.view.all'], <RequisitionsPage />)} />
        <Route path="/requisitions/new" element={guard(['requisition.create'], <RequisitionNewPage />)} />
        <Route path="/requisitions/:id" element={guard(['requisition.create', 'requisition.view.all'], <RequisitionDetailPage />)} />

        <Route path="/orders" element={guard(['po.view.all', 'requisition.create'], <OrdersPage />)} />
        <Route path="/orders/:id" element={guard(['po.view.all', 'requisition.create'], <OrderDetailPage />)} />
        <Route path="/receipts" element={guard(['grn.record', 'po.view.all'], <ReceiptsPage />)} />

        <Route path="/suppliers" element={guard(['supplier.view'], <SuppliersPage />)} />
        <Route path="/suppliers/:id" element={guard(['supplier.view'], <SupplierDetailPage />)} />

        <Route path="/invoices" element={guard(['ap.view'], <InvoicesPage />)} />
        <Route path="/invoices/:id" element={guard(['ap.view'], <InvoiceDetailPage />)} />
        <Route path="/payments" element={guard(['ap.view', 'ap.payment.prepare', 'ap.payment.approve'], <PaymentsPage />)} />
        <Route path="/payments/:id" element={guard(['ap.view', 'ap.payment.prepare', 'ap.payment.approve'], <PaymentRunDetailPage />)} />

        <Route path="/sales" element={guard(['ar.view', 'ar.request'], <SalesPage />)} />
        <Route path="/debtors" element={guard(['ar.view'], <DebtorsPage />)} />

        <Route path="/ledger" element={guard(['gl.view'], <LedgerPage />)} />
        <Route path="/ledger/journals/:id" element={guard(['gl.view'], <JournalDetailPage />)} />
        <Route path="/budgets" element={guard(['budget.view.own', 'budget.view.all'], <BudgetsPage />)} />
        <Route path="/cash" element={guard(['cash.view'], <CashPage />)} />
        <Route path="/assets" element={guard(['assets.view'], <AssetsPage />)} />
        <Route path="/projects" element={guard(['project.view'], <ProjectsPage />)} />

        <Route path="/reports" element={guard(['report.run'], <ReportsPage />)} />
        <Route path="/reports/:code" element={guard(['report.run'], <ReportsPage />)} />
        <Route path="/contracts" element={guard(['contract.view'], <ContractsPage />)} />
        <Route path="/contracts/:id" element={guard(['contract.view'], <ContractDetailPage />)} />
        <Route path="/planning" element={guard(['plan.view'], <PlanningPage />)} />
        <Route path="/assistant" element={guard(['ai.use'], <AssistantPage />)} />

        <Route path="/close" element={guard(['close.view'], <ClosePage />)} />
        <Route path="/integrations" element={guard(['integration.view'], <IntegrationsPage />)} />
        <Route path="/migration" element={guard(['migration.manage'], <MigrationPage />)} />
        <Route path="/audit" element={guard(['audit.view'], <AuditPage />)} />
        <Route path="/self-service" element={<SelfServicePage />} />
        <Route path="/support" element={guard(['support.raise'], <SupportPage />)} />
        <Route path="/admin" element={guard(['admin.settings', 'platform.tenant.view', 'workflow.configure'], <AdminPage />)} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
    </Shell>
  );
}
